import { FC, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Text,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useTheme,
} from '@chakra-ui/react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { AuthorIdFilter, TeamFilter } from '../../../shared/utils';
import type { ColumnDef } from '@tanstack/react-table';
import { NewDataGrid } from '../../../NewGridTable/NewDataGrid';
import type { TestRunMetricDetailItem } from '../../types/charts.types';
import { MetricModalLoading } from './DefaultDetailsModal/MetricModalLoading';
import TestStatsItem from '../../../TestStatsItem';
import {
  IoMdCheckmarkCircleOutline,
  IoMdCloseCircleOutline,
} from 'react-icons/io';
import { FiSkipForward } from 'react-icons/fi';
import { apiClient, getOrgId, getToken } from '@devd-client/api';

interface TestRunDetailsModalData {
  data: TestRunMetricDetailItem[];
}

interface TestRunDetailsModalProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  defaultData?: TestRunDetailsModalData;
  heading: string;
  subHeading: string;
  isFetching: boolean;
  handlePageClick: (page: number) => void;
  currentChartMeta: any;
  querykey?: string;
  startDate?: string;
  endDate?: string;
  filterBy: AuthorIdFilter | TeamFilter;
  payload: any;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

const TestRunDetailsModal: FC<TestRunDetailsModalProps> = ({
  openDrawer,
  handleDrawerClose,
  defaultData,
  heading,
  subHeading,
  isFetching,
  handlePageClick,
  currentChartMeta,
  querykey,
  startDate,
  endDate,
  filterBy,
  payload,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) => {
  const { colors } = useTheme();
  const [pending, setPending] = useState(false);
  const rows: TestRunMetricDetailItem[] = useMemo(
    () => defaultData?.data || [],
    [defaultData?.data]
  );

  const hasTestRunId = useMemo(
    () => rows?.some((item) => (item as any)?.testRunId),
    [rows]
  );

  const hasMetricValue = useMemo(
    () =>
      rows?.some(
        (item: any) =>
          item.metricValue !== undefined && item.metricValue !== null
      ),
    [rows]
  );

  const hasTotalTests = useMemo(
    () =>
      rows?.some(
        (item: any) =>
          item.total_tests !== undefined && item.total_tests !== null
      ),
    [rows]
  );

  const hasTestResults = useMemo(
    () =>
      rows?.some((item: any) => {
        const total = Number(item.total_tests ?? 0);
        const passed = Number(item.passed_tests ?? 0);
        const failed = Number(item.failed_tests ?? 0);
        const skipped = Number(item.skipped_tests ?? 0);
        return total > 0 || passed > 0 || failed > 0 || skipped > 0;
      }),
    [rows]
  );

  const download = (data: any) => {
    const blob = new Blob([data], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute(
      'download',
      `${getOrgId()} - ${currentChartMeta?.chartTitle || 'test-run-details'}`
    );
    a.click();
  };

  const downloadTestSuiteDetailsCSV = async () => {
    setPending(true);
    apiClient(
      `/v1/csv/${currentChartMeta?.chartKey}/${
        ['TEST_SUCCESS_RATE', 'TEST_RUN_COUNT', 'TEST_RUN_TIME'].includes(
          currentChartMeta?.chartKey
        )
          ? 'test-run-details'
          : 'test-unit-detail'
      }`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          authorization: `bearer ${getToken()}`,
        },
        body: JSON.stringify({
          orgId: `${getOrgId()}`,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          startDate: startDate,
          endDate: endDate,
          vsmAtrributes: { age: (filterBy as any)?.age },
        }),
      }
    )
      .then((res: any) => {
        setPending(false);
        download(res);
      })
      .catch(() => {
        setPending(false);
      });
  };

  const columns: ColumnDef<TestRunMetricDetailItem, any>[] = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        enableSorting: false,
        size: 300,
        cell: ({ row }) => {
          const item = row.original;
          if (!item?.name) return '-';
          if (item?.externalUrl) {
            return (
              <Link to={item.externalUrl}>
                <Text
                  fontSize="sm"
                  fontFamily="heading"
                  color="primary.500"
                  fontWeight="medium"
                  textDecoration="underline"
                  noOfLines={1}
                >
                  {item.name}
                </Text>
              </Link>
            );
          }
          return (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
            >
              {item.name}
            </Text>
          );
        },
      },
      {
        header: 'Project',
        accessorKey: 'projectId',
        enableSorting: false,
        size: 200,
        cell: ({ row }) => {
          const item = row.original;
          if (!item?.projectId) return '-';
          if (item?.repo) {
            return (
              <Link to={item.repo} target="_blank" rel="noopener">
                <Text
                  fontSize="sm"
                  fontFamily="heading"
                  color="primary.500"
                  fontWeight="medium"
                  textDecoration="underline"
                  noOfLines={1}
                  wordBreak="break-all"
                >
                  {item.projectId}
                </Text>
              </Link>
            );
          }
          return (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
            >
              {item.projectId}
            </Text>
          );
        },
      },
      {
        header: 'ID',
        accessorKey: 'id',
        enableSorting: false,
        size: 150,
        cell: ({ row }) => {
          const item = row.original;
          return item?.id ? (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
            >
              {item.id}
            </Text>
          ) : (
            '-'
          );
        },
      },
      ...(hasTestRunId
        ? [
            {
              header: 'Test Run ID',
              accessorKey: 'testRunId',
              enableSorting: false,
              size: 260,
              cell: ({ row }) => {
                const value = (row.original as any)?.testRunId;
                if (!value) return '-';
                return (
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    color="text.secondary"
                    fontWeight="medium"
                    noOfLines={1}
                    wordBreak="break-all"
                  >
                    {value}
                  </Text>
                );
              },
            } as ColumnDef<TestRunMetricDetailItem, any>,
          ]
        : []),
      {
        header: 'Status',
        accessorKey: 'status',
        enableSorting: false,
        size: 120,
        cell: ({ row }) => {
          const item = row.original;
          return item?.status ? (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
            >
              {item.status}
            </Text>
          ) : (
            '-'
          );
        },
      },
      {
        header: 'Type',
        accessorKey: 'type',
        enableSorting: false,
        size: 120,
        cell: ({ row }) => {
          const item = row.original;
          return item?.type ? (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
            >
              {item.type}
            </Text>
          ) : (
            '-'
          );
        },
      },
      {
        header: 'Started At',
        accessorKey: 'started_at',
        enableSorting: false,
        size: 180,
        cell: ({ row }) => {
          const item = row.original;
          if (!item?.started_at) return '-';
          const formatted = moment(item.started_at).format(
            'DD MMM YYYY, h:mm A'
          );
          return (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
              whiteSpace="nowrap"
            >
              {formatted}
            </Text>
          );
        },
      },
      {
        header: 'Completed At',
        accessorKey: 'completed_At',
        enableSorting: false,
        size: 180,
        cell: ({ row }) => {
          const item = row.original;
          if (!item?.completed_At) return '-';
          const formatted = moment(item.completed_At).format(
            'DD MMM YYYY, h:mm A'
          );
          return (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
              whiteSpace="nowrap"
            >
              {formatted}
            </Text>
          );
        },
      },
      ...(currentChartMeta?.chartKey !== 'TEST_RUN_COUNT' && hasMetricValue
        ? [
            {
              header:
                currentChartMeta?.chartKey === 'TEST_SUCCESS_RATE'
                  ? 'Test Success Rate'
                  : currentChartMeta?.chartKey === 'TEST_RUN_TIME'
                  ? 'Average Cycle Time'
                  : 'Metric Value',
              accessorKey: 'metricValue',
              enableSorting: false,
              size: 150,
              cell: ({ row }) => {
                const item = row.original;
                if (item.metricValue === undefined && item.metricValue !== 0)
                  return '-';
                return (
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    {String(item.metricValue)}
                  </Text>
                );
              },
            } as ColumnDef<TestRunMetricDetailItem, any>,
          ]
        : []),
      ...(hasTotalTests
        ? [
            {
              header: 'Total Tests',
              accessorKey: 'total_tests',
              enableSorting: false,
              size: 120,
              cell: ({ row }) => {
                const item = row.original;
                return item?.total_tests ? (
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    {item.total_tests}
                  </Text>
                ) : (
                  '-'
                );
              },
            } as ColumnDef<TestRunMetricDetailItem, any>,
          ]
        : []),
      ...(hasTestResults
        ? [
            {
              header: 'Test Results',
              accessorKey: 'testResults',
              enableSorting: false,
              size: 300,
              cell: ({ row }) => {
                const item = row.original;
                const totalTests = Number(item.total_tests ?? 0);
                const passedTests = Number(item.passed_tests ?? 0);
                const failedTests = Number(item.failed_tests ?? 0);
                const skippedTests = Number(item.skipped_tests ?? 0);

                const passedPct =
                  totalTests > 0 ? (passedTests / totalTests) * 100 : 0;
                const failedPct =
                  totalTests > 0 ? (failedTests / totalTests) * 100 : 0;
                const skippedPct =
                  totalTests > 0 ? (skippedTests / totalTests) * 100 : 0;

                if (totalTests === 0) return '-';

                return (
                  <Flex gap={4} align="center">
                    <TestStatsItem
                      icon={IoMdCheckmarkCircleOutline}
                      label="Passed"
                      count={passedTests}
                      color={colors.success}
                      percentage={passedPct}
                    />
                    <TestStatsItem
                      icon={IoMdCloseCircleOutline}
                      label="Failed"
                      count={failedTests}
                      color={colors.error}
                      percentage={failedPct}
                    />
                    <TestStatsItem
                      icon={FiSkipForward}
                      label="Skipped"
                      count={skippedTests}
                      color={colors.warning}
                      percentage={skippedPct}
                    />
                  </Flex>
                );
              },
            } as ColumnDef<TestRunMetricDetailItem, any>,
          ]
        : []),
    ],
    [
      currentChartMeta?.chartKey,
      colors,
      hasMetricValue,
      hasTotalTests,
      hasTestResults,
      hasTestRunId,
    ]
  );

  return (
    <Modal
      isOpen={openDrawer}
      onClose={handleDrawerClose}
      size="6xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader pb={2}>
          <Flex justify="space-between" align="flex-start" gap={4}>
            <Stack spacing={1}>
              <Text
                fontFamily="heading"
                fontSize="lg"
                fontWeight="bold"
                color="text.primary"
              >
                {heading}
              </Text>
              <Text
                fontFamily="heading"
                fontSize="sm"
                color="text.secondary"
                fontWeight="medium"
              >
                {subHeading}
              </Text>
            </Stack>
            <Flex align="center" gap={2} mr={6}>
              <Button
                variant="tertiary"
                size="sm"
                h="32px"
                px={4}
                borderRadius="full"
                onClick={downloadTestSuiteDetailsCSV}
                isLoading={pending}
                minW="120px"
              >
                Download Data
              </Button>
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {isFetching && !defaultData?.data ? (
              <MetricModalLoading />
            ) : rows && rows.length > 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
              >
                <NewDataGrid
                  data={rows}
                  columns={columns}
                  showLoader={false}
                  skeletonLoader={false}
                  sticky="firstCol"
                  useMaxH
                  showGridLines
                  infiniteScroll
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                />
              </Box>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TestRunDetailsModal;
